// FIRUL pe care stă ecranul, citit din rută și din registru — același pentru
// lanțul din antet, șină, vizualizator, verificări și costuri (A5: «Verificări,
// Costuri și Dosar citesc același fir»).

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useRegistruOptional } from "./Registru";
import type { SolutieRand } from "./api";
import { DISCIPLINA_PAGINII, FIR_GOL, citesteRuta, firulRutei, type Fir,
         type Ruta } from "./selectie";

/** De ce nu se poate arăta rândul din cale: un id necunoscut, sau unul al
 *  altei discipline decât ruta. Se SPUNE, cu drum spre soluția implicită —
 *  nu se aruncă în tăcere și nu se redirecționează (Î24). */
export type EroareRuta = "necunoscuta" | "alta_disciplina";

export interface FirulEcranului {
  ruta: Ruta;
  fir: Fir;
  /** rândul din cale, dacă e în registru */
  rand: SolutieRand | null;
  eroare: EroareRuta | null;
  /** registrul a sosit, iar rândul din cale, dacă lipsea, a fost căutat din
   *  nou; până atunci ecranul se încarcă, nu spune «nu există» */
  incarcat: boolean;
}

export function useFir(): FirulEcranului {
  const { pathname, search } = useLocation();
  const reg = useRegistruOptional();
  const registru = reg?.registru ?? null;
  const ruta = useMemo(() => citesteRuta(pathname, search), [pathname, search]);
  // Un id pe care registrul ținut nu-l are se caută o dată din nou (v.
  // `Registru`, «un id din adresă…»): rândul abia creat nu e încă în el.
  const lipseste = !!registru && !!ruta.sol
    && !registru.solutii.some((s) => s.id === ruta.sol);
  const negasit = lipseste && (reg ? reg.negasit(ruta.sol!) : true);
  const cautaDinNou = reg?.cautaDinNou;
  useEffect(() => {
    if (lipseste && !negasit && ruta.sol) cautaDinNou?.(ruta.sol);
  }, [lipseste, negasit, ruta.sol, cautaDinNou]);
  return useMemo(() => {
    const rand = registru && ruta.sol
      ? registru.solutii.find((s) => s.id === ruta.sol) ?? null : null;
    const asteptata = DISCIPLINA_PAGINII[ruta.pagina];
    let eroare: EroareRuta | null = null;
    if (registru && ruta.sol) {
      if (!rand) eroare = negasit ? "necunoscuta" : null;
      else if (asteptata && rand.discipline !== asteptata) eroare = "alta_disciplina";
      else if (ruta.pagina === "costuri" && rand.discipline !== "structure") {
        eroare = "alta_disciplina";
      }
    }
    return {
      ruta,
      fir: registru ? firulRutei(registru, ruta) : FIR_GOL,
      rand: eroare ? null : rand,
      eroare,
      incarcat: !!registru && (!lipseste || negasit),
    };
  }, [registru, ruta, lipseste, negasit]);
}

// --- ultima rută privită ---------------------------------------------------------
//
// `/p/:id` deschide ce a privit vizitatorul ultima oară (A4). E o comoditate a
// aparatului, nu o stare a proiectului: stă în localStorage, iar o stocare
// blocată doar o uită.

const cheie = (projectId: string) => `ob-ultima-ruta-${projectId}`;

export function ultimaRuta(projectId: string): string | null {
  try {
    return localStorage.getItem(cheie(projectId));
  } catch {
    return null;
  }
}

export function amintesteRuta(projectId: string, cale: string): void {
  try {
    localStorage.setItem(cheie(projectId), cale);
  } catch {
    // o fereastră privată sau o stocare plină: se pierde doar comoditatea
  }
}
